import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import surveyService from '../services/surveyService';
import '../styles/Survey.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Tooltip, Divider } from '@mui/material';

const Survey = () => {
  const navigate = useNavigate();
  const studentPkStr = localStorage.getItem('student_pk');
  const studentId = studentPkStr ? parseInt(studentPkStr, 10) : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);

  const [verbalRating, setVerbalRating] = useState(5);
  const [verbalReason, setVerbalReason] = useState('');
  const [mathRating, setMathRating] = useState(5);
  const [mathReason, setMathReason] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const submittedThisWeek = !!status?.submittedThisWeek || !!status?.SubmittedThisWeek;
  const weekStart = status?.weekStart || status?.WeekStart;
  const existing = status?.submission || status?.Submission;
  const readOnly = submittedThisWeek;

  // Format week range
  const formatWeekRange = () => {
    if (!weekStart) return '';
    try {
      const start = new Date(weekStart);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const opts = { month: 'short', day: 'numeric' };
      return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', opts)}, ${start.getFullYear()}`;
    } catch {
      return '';
    }
  };

  const starLabels = ['Very Poor', 'Poor', 'Average', 'Good', 'Excellent'];

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!studentId) {
        setError('Missing student session. Please login again.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const s = await surveyService.getStatus(studentId);
        if (!mounted) return;
        setStatus(s);
        if (s?.submission || s?.Submission) {
          const sub = s.submission || s.Submission;
          setVerbalRating(sub.VerbalRating || sub.verbalRating || 5);
          setVerbalReason(sub.VerbalReason || sub.verbalReason || '');
          setMathRating(sub.MathRating || sub.mathRating || 5);
          setMathReason(sub.MathReason || sub.mathReason || '');
        }
      } catch (e) {
        setError('Failed to load survey status.');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [studentId]);

  const validate = () => {
    if (verbalRating < 3 && !verbalReason.trim()) {
      setError('Please provide a reason for verbal rating below 3.');
      return false;
    }
    if (mathRating < 3 && !mathReason.trim()) {
      setError('Please provide a reason for math rating below 3.');
      return false;
    }
    setError('');
    return true;
  };

  const handleReviewClick = (e) => {
    e.preventDefault();
    if (readOnly) {
      setShowConfirmModal(true);
      return;
    }
    if (!validate()) return;
    setShowReviewModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!studentId) return;
    try {
      setSaving(true);
      setShowReviewModal(false);
      await surveyService.submit({
        studentId,
        verbalRating,
        verbalReason,
        mathRating,
        mathReason,
      });
      const updated = await surveyService.getStatus(studentId);
      setStatus(updated);
    } catch (e) {
      if (e?.response?.status === 409) {
        setShowConfirmModal(true);
        const updated = await surveyService.getStatus(studentId);
        setStatus(updated);
      } else {
        setError(e?.response?.data?.message || 'Failed to submit survey.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="survey-container">
      <div className="survey-card">
        <div className="survey-header">
          <div>
            <h1>Weekly Teacher Survey</h1>
            <p className="subtitle">Answer 2 quick questions about your Verbal and Math teachers.</p>
            {formatWeekRange() && (
              <p className="week-range" style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                📅 Survey Period: <strong>{formatWeekRange()}</strong>
              </p>
            )}
          </div>
          <div className={`status-pill ${submittedThisWeek ? 'done' : 'pending'}`}>
            {submittedThisWeek ? 'Submitted for this week' : 'Pending this week'}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <form onSubmit={handleReviewClick}>
            {readOnly && (
              <div className="info-msg">✅ You have already submitted this week's survey. You can view your answers below.</div>
            )}
            <div className="question-block">
              <label className="q-title">Rate the quality of your Verbal lessons</label>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map(star => (
                  <Tooltip key={star} title={`${star} - ${starLabels[star - 1]}`} arrow placement="top">
                    <button
                      type="button"
                      className={`star ${verbalRating >= star ? 'filled' : ''}`}
                      disabled={readOnly}
                      onClick={() => { if (!readOnly) setVerbalRating(star); }}
                    >
                      ★
                    </button>
                  </Tooltip>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Typography variant="body2" color="text.secondary">
                  {verbalRating}/5 - {starLabels[verbalRating - 1]}
                </Typography>
              </div>
              <div className="rating-labels">
                <span>Not satisfied</span>
                <span>Extremely satisfied</span>
              </div>
              {verbalRating < 3 && (
                <div className="reason">
                  <label>Please tell us why you gave a low rating:</label>
                  <textarea value={verbalReason} onChange={(e) => setVerbalReason(e.target.value)} placeholder="Please explain briefly" rows={3} disabled={readOnly} readOnly={readOnly} />
                </div>
              )}
            </div>

            <div className="question-block">
              <label className="q-title">Rate the quality of your Math lessons</label>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map(star => (
                  <Tooltip key={star} title={`${star} - ${starLabels[star - 1]}`} arrow placement="top">
                    <button
                      type="button"
                      className={`star ${mathRating >= star ? 'filled' : ''}`}
                      disabled={readOnly}
                      onClick={() => { if (!readOnly) setMathRating(star); }}
                    >
                      ★
                    </button>
                  </Tooltip>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Typography variant="body2" color="text.secondary">
                  {mathRating}/5 - {starLabels[mathRating - 1]}
                </Typography>
              </div>
              <div className="rating-labels">
                <span>Not satisfied</span>
                <span>Extremely satisfied</span>
              </div>
              {mathRating < 3 && (
                <div className="reason">
                  <label>Please tell us why you gave a low rating:</label>
                  <textarea value={mathReason} onChange={(e) => setMathReason(e.target.value)} placeholder="Please explain briefly" rows={3} disabled={readOnly} readOnly={readOnly} />
                </div>
              )}
            </div>

            {error && <div className="error-msg">{error}</div>}

            <div className="actions">
              <button type="button" className="secondary" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
              <button type="submit" className="primary" disabled={saving}>
                {saving ? 'Submitting...' : readOnly ? 'View Submission' : 'Review & Submit'}
              </button>
            </div>
          </form>
        )}

        {/* Review Modal */}
        <Dialog open={showReviewModal} onClose={() => setShowReviewModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Review Your Answers</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="h6" gutterBottom>Verbal Teacher</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>Rating: {verbalRating}/5 - {starLabels[verbalRating - 1]}</Typography>
              {verbalRating < 3 && verbalReason && (
                <Typography variant="body2" color="text.secondary">Reason: {verbalReason}</Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Math Teacher</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>Rating: {mathRating}/5 - {starLabels[mathRating - 1]}</Typography>
              {mathRating < 3 && mathReason && (
                <Typography variant="body2" color="text.secondary">Reason: {mathReason}</Typography>
              )}

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  ⚠️ Once submitted, you cannot change your answers for this week.
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowReviewModal(false)}>Go Back</Button>
            <Button variant="contained" onClick={handleConfirmSubmit} disabled={saving}>
              {saving ? 'Submitting...' : 'Confirm & Submit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Already Submitted Modal */}
        <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>✅ Already Submitted</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              You have already submitted this week's survey.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Survey Period: <strong>{formatWeekRange()}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You can view your answers on this page, but you cannot submit again until next week.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setShowConfirmModal(false)}>Got it</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Survey;
